import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";
import type { Spark, VoteType } from "@sparklab/types";
import { api } from "../api/client";
import { getThemeStyles, DEFAULT_THEME } from "../themeConfig";
import { LikeButton } from "../components/LikeButton";
import { ShareButton } from "../components/ShareButton";
import { encodeSparkId } from "../utils/sparkId";

export default function HomePage() {
  const { isAuthenticated, user, logout, refreshCurrentUser } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const themeKey = user?.theme ?? DEFAULT_THEME;
  const t = getThemeStyles(themeKey);
  const [discoverSparks, setDiscoverSparks] = useState<Spark[]>([]);
  const [discoverLoading, setDiscoverLoading] = useState(true);
  const [discoverError, setDiscoverError] = useState<string | null>(null);
  const [votingSparkId, setVotingSparkId] = useState<number | null>(null);
  const [showFriendsTooltip, setShowFriendsTooltip] = useState(false);
  const [homeShareUrl, setHomeShareUrl] = useState("");
  const friendsButtonRef = useRef<HTMLDivElement | null>(null);
  const friendsTooltipRef = useRef<HTMLDivElement | null>(null);

  const handleOpenSpark = (sparkId: number) => {
    navigate(`/sparks/${encodeSparkId(sparkId)}`);
  };

  const handleCreateClick = () => {
    if (isAuthenticated) {
      navigate("/create");
    } else {
      navigate("/login");
    }
  };

  const handleMySparksClick = () => {
    if (isAuthenticated) {
      navigate("/my-sparks");
    } else {
      navigate("/login");
    }
  };

  const handlePublicSparksClick = () => {
    navigate("/sparks");
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  useEffect(() => {
    let cancelled = false;

    const fetchPublicSparks = async () => {
      try {
        setDiscoverLoading(true);
        const data = await api.getPublicSparks(9);
        if (!cancelled) {
          setDiscoverSparks(data);
          setDiscoverError(null);
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : "获取公开作品失败";
          setDiscoverError(message);
        }
      } finally {
        if (!cancelled) {
          setDiscoverLoading(false);
        }
      }
    };

    fetchPublicSparks();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") {
      setHomeShareUrl(window.location.origin);
    }
  }, []);

  // 每次进入首页时刷新用户信息，确保 points 等数据最新
  useEffect(() => {
    if (isAuthenticated) {
      refreshCurrentUser().catch(() => {
        /* 已在 refreshCurrentUser 内部处理 */
      });
    }
  }, [isAuthenticated, refreshCurrentUser]);

  useEffect(() => {
    if (!showFriendsTooltip) {
      return;
    }
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        friendsButtonRef.current?.contains(target) ||
        friendsTooltipRef.current?.contains(target)
      ) {
        return;
      }
      setShowFriendsTooltip(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showFriendsTooltip]);

  const handleVote = async (sparkId: number, vote: VoteType) => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }

    try {
      setVotingSparkId(sparkId);
      const updated = await api.voteSpark(sparkId, vote);
      setDiscoverSparks((prev) =>
        [...prev]
          .map((spark) => (spark.id === updated.id ? updated : spark))
          .sort((a, b) => {
            if (b.likes === a.likes) {
              return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
            }
            return b.likes - a.likes;
          })
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : "投票失败";
      showToast(message, "error");
    } finally {
      setVotingSparkId(null);
    }
  };

  return (
    <div className={`min-h-screen ${t.bg} text-white p-6 font-mono transition-all duration-300`}>
      <div className="flex justify-between items-center mb-6 gap-3 flex-wrap">
        <div className={`flex flex-wrap items-baseline gap-3 text-3xl font-bold tracking-wide drop-shadow-lg ${t.header}`}>
          <span>✨ 魔法工场</span>
          <span className="text-base md:text-lg font-semibold tracking-normal opacity-90 border-l border-white/40 pl-3">
            你的创意游乐场
          </span>
        </div>
        <div className="flex gap-3">
          {isAuthenticated ? (
            <>
              <button
                onClick={() => navigate("/profile")}
                className={`px-4 py-2 rounded-xl border-2 font-bold ${t.button2} hover:scale-105 transition`}
              >
                个人中心
              </button>
              <button
                onClick={handleLogout}
                className={`px-4 py-2 rounded-xl border-2 font-bold ${t.button3} hover:scale-105 transition`}
              >
                退出登录
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className={`px-4 py-2 rounded-xl border-2 font-bold ${t.button1} hover:scale-105 transition`}
            >
              登录 / 注册
            </button>
          )}
        </div>
      </div>

      {/* HEADER */}
      {/* <header className="text-center mb-10">
        <p className={`text-lg ${t.subheader}`}>你的创意游乐场</p>
      </header> */}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
        {/* PROFILE CARD */}
        <div className={`col-span-1 flex flex-col items-center p-6 rounded-3xl shadow-xl border-4 ${t.card}`}>
          <div className="w-32 h-32 bg-black bg-opacity-20 border-4 border-white rounded-xl flex items-center justify-center text-4xl">
            {isAuthenticated ? "👤" : "🧒"}
          </div>
          <h2 className={`text-2xl mt-4 font-bold ${t.subheader}`}>
            {isAuthenticated ? `Hi, ${user?.user_name}!` : "Hi, Creator!"}
          </h2>
          {isAuthenticated && user && (
            <>
              <p className={`text-sm mt-2 ${t.subheader}`}>💎: {user.points}</p>
              
            </>
          )}
        </div>

        {/* FEATURE BUTTONS */}
        <div className="col-span-2 grid grid-cols-2 gap-4">
          <button
            onClick={handleCreateClick}
            className={`${t.button1} rounded-3xl p-6 text-2xl font-bold hover:scale-105 transition border-2`}
          >
            开始创作
          </button>

          <button
            onClick={handleMySparksClick}
            className={`${t.button2} rounded-3xl p-6 text-2xl font-bold hover:scale-105 transition border-2`}
          >
            我的作品
          </button>

          <button
            onClick={handlePublicSparksClick}
            className={`${t.button3} rounded-3xl p-6 text-2xl font-bold hover:scale-105 transition border-2`}
          >
            公开作品
          </button>

          <div className="relative h-full" ref={friendsButtonRef}>
            <button
              onClick={() => setShowFriendsTooltip((prev) => !prev)}
              className={`${t.button4} rounded-3xl p-6 text-2xl font-bold hover:scale-105 transition border-2 w-full h-full`}
            >
              和朋友一起玩
            </button>
            {showFriendsTooltip && (
              <div
                ref={friendsTooltipRef}
                className="absolute left-1/2 top-full z-30 mt-4 w-[min(320px,85vw)] -translate-x-1/2 rounded-3xl border-2 border-white/30 bg-black/80 p-4 shadow-2xl backdrop-blur-xl"
              >
                <div className="flex items-center justify-between gap-3">
                  <p className="text-sm font-semibold tracking-wide">邀请朋友一起玩</p>
                  <button
                    onClick={() => setShowFriendsTooltip(false)}
                    className="text-white/70 hover:text-white text-lg leading-none"
                    aria-label="关闭分享提示"
                  >
                    ×
                  </button>
                </div>
                <p className="mt-1 text-xs text-white/70">
                  把首页链接发给好友，大家一起逛魔法工场吧！
                </p>
                <div className="mt-3 rounded-2xl bg-white/5 p-3 border border-white/10">
                  <ShareButton
                    url={homeShareUrl || (typeof window !== "undefined" ? window.location.origin : "")}
                    title="魔法工场 · 创意游乐场"
                    description="快来和我一起在魔法工场创作脑洞吧！"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* EXPLORE SECTION */}
      <section className="mt-12 max-w-5xl mx-auto">
        <h3 className={`text-3xl font-bold mb-4 ${t.header}`}>🔍 发现更多</h3>
        {discoverLoading ? (
          <p className="text-sm opacity-80">正在召唤最酷的作品...</p>
        ) : discoverError ? (
          <p className="text-red-200">{discoverError}</p>
        ) : discoverSparks.length === 0 ? (
          <p className="text-sm opacity-80">还没有公开作品，快去成为第一个吧！</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {discoverSparks.map((spark) => (
              <div
                key={spark.id}
                role="button"
                tabIndex={0}
                onClick={() => handleOpenSpark(spark.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleOpenSpark(spark.id);
                  }
                }}
                className={`p-4 rounded-2xl border-4 shadow-lg flex flex-col cursor-pointer focus:outline-none focus:ring-2 focus:ring-white/80 ${t.exploreCard}`}
              >
                <div className="flex justify-between items-center mb-2">
                  <p className={`text-lg font-bold ${t.subheader}`}>{spark.name}</p>
                  <span className="text-xs opacity-70">
                    {new Date(spark.created_at).toLocaleDateString("zh-CN", { month: "short", day: "numeric" })}
                  </span>
                </div>
                <p className="text-sm opacity-80 flex-1 max-h-20 overflow-hidden text-ellipsis">{spark.prompt}</p>
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <LikeButton
                    direction="up"
                    count={spark.likes}
                    loading={votingSparkId === spark.id}
                    disabled={votingSparkId === spark.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleVote(spark.id, "like");
                    }}
                  />
                  <LikeButton
                    direction="down"
                    count={spark.unlikes}
                    loading={votingSparkId === spark.id}
                    disabled={votingSparkId === spark.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleVote(spark.id, "unlike");
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

